import React from 'react';
import { Box, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { en } from '../translations/en';
import { Field } from './edit-object/design/Field';

type FieldVariant = 'mui' | 'design';

interface DatePickerFieldProps {
  dateType: string;
  value?: string;
  onChange?: (value: string) => void;
  onAccept?: () => void;
  onBlur?: () => void;
  /**
   * 'mui' (default) renders the MUI DatePicker / TextField with their
   * float-on-focus notched labels. 'design' wraps each input in the
   * shared Field primitive so labels are always visible and uppercase.
   */
  variant?: FieldVariant;
}

const normalizeDateType = (dateType: string): string => {
  if (dateType === 'Date/Month/Year') return 'Month/Day/Year';
  return dateType;
};

const YEAR_ONLY_TYPES = ['Year', 'Circa', 'Date Range', 'Date Range (Circa)'];

const isYearOnly = (normalizedType: string): boolean =>
  YEAR_ONLY_TYPES.includes(normalizedType);

export const DatePickerField = ({ dateType, value = '', onChange, onAccept, onBlur, variant = 'mui' }: DatePickerFieldProps) => {
  const theme = useTheme();
  const normalizedType = normalizeDateType(dateType);

  // ── Year-only branch (Year, Circa, Date Range, Date Range (Circa)) ──────────
  // MUI DatePicker with views={['year']} renders a section-mask input that shows
  // "YYYY" placeholder sections. Users must first click the section to highlight
  // it before typing, which causes the field to clear on invalid partial input.
  // For year-only types we render plain <TextField type="number"> so users can
  // type a four-digit year directly without any template interference (#977).
  if (isYearOnly(normalizedType)) {
    return (
      <YearOnlyField
        normalizedType={normalizedType}
        value={value}
        onChange={onChange}
        onAccept={onAccept}
        onBlur={onBlur}
        variant={variant}
      />
    );
  }

  // ── Full date / month+year branch ──────────────────────────────────────────
  const parseValue = (val: string) => {
    if (!val) return { start: null, end: null };

    const cleanVal = val.replace(/^ca?\. /, '');
    return {
      start: cleanVal ? dayjs(cleanVal) : null,
      end: null
    };
  };

  const { start: startDate, end: endDate } = parseValue(value);

  const getStorageFormat = () => {
    if (normalizedType === 'Month/Year') return 'YYYY-MM';
    return 'YYYY-MM-DD';
  };

  const updateValue = (newStart: Dayjs | null, newEnd: Dayjs | null = endDate) => {
    if (!newStart) {
      onChange?.('');
      return;
    }

    const format = getStorageFormat();

    if (newEnd) {
      onChange?.(`${newStart.format(format)} - ${newEnd.format(format)}`);
    } else {
      onChange?.(newStart.format(format));
    }
  };

  const formatPreview = () => {
    if (!startDate) return '';

    switch (normalizedType) {
      case 'Month/Day/Year':
        return startDate.format('MM/DD/YYYY');
      case 'Month/Year':
        return startDate.format('MM/YYYY');
      default:
        return '';
    }
  };

  const getViews = (): ('year' | 'month' | 'day')[] => {
    if (normalizedType === 'Month/Year') return ['year', 'month'];
    return ['year', 'month', 'day'];
  };

  const getOpenTo = (): 'year' | 'month' | 'day' => {
    if (normalizedType === 'Month/Year') return 'year';
    return 'day';
  };

  const views = getViews();
  const openTo = getOpenTo();

  if (!dateType) return null;

  const datePickerEl = (
    <DatePicker
      label={variant === 'design' ? undefined : en.datePicker.date}
      value={startDate}
      onChange={(newDate) => updateValue(newDate, endDate)}
      onAccept={() => onAccept?.()}
      views={views}
      openTo={openTo}
      maxDate={dayjs()}
      slotProps={{
        textField: {
          fullWidth: true,
          onBlur: () => onBlur?.(),
          ...(variant === 'design' ? { variant: 'standard', InputProps: { disableUnderline: true } } : {}),
        }
      }}
    />
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={variant === 'design' ? undefined : { mt: 2 }}>
        <Box sx={variant === 'design' ? { display: 'flex', gap: 2 } : { display: 'flex', gap: 2, mb: 2 }}>
          {variant === 'design' ? (
            <Box sx={{ flexGrow: 1 }}>
              <Field label={en.datePicker.date}>{datePickerEl}</Field>
            </Box>
          ) : (
            datePickerEl
          )}
        </Box>

        {startDate && variant !== 'design' && (
          <TextField
            fullWidth
            label={en.datePicker.preview}
            value={formatPreview()}
            variant="outlined"
            InputProps={{ readOnly: true }}
            sx={{
              '& .MuiInputBase-input': {
                color: theme.palette.text.secondary
              }
            }}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};

// ── YearOnlyField ─────────────────────────────────────────────────────────────
// Renders plain TextField(s) with type="number" for year-only date types.
// This avoids the MUI DatePicker section-mask that makes it hard to type a year.

interface YearOnlyFieldProps {
  normalizedType: string;
  value: string;
  onChange?: (value: string) => void;
  onAccept?: () => void;
  onBlur?: () => void;
  variant?: FieldVariant;
}

const parseYearOnlyValue = (
  normalizedType: string,
  val: string
): { startYear: string; endYear: string } => {
  if (!val) return { startYear: '', endYear: '' };

  const isCirca = normalizedType.includes('Circa');
  const cleanVal = isCirca ? val.replace(/^ca?\. /, '') : val;

  if (val.includes(' - ')) {
    const [startStr, endStr] = cleanVal.split(' - ');
    return { startYear: startStr ?? '', endYear: endStr ?? '' };
  }

  return { startYear: cleanVal, endYear: '' };
};

const buildYearOnlyValue = (
  normalizedType: string,
  startYear: string,
  endYear: string
): string => {
  if (!startYear || startYear.length < 4) return '';

  const isCirca = normalizedType.includes('Circa');
  const prefix = isCirca ? 'ca. ' : '';
  const isDateRange = normalizedType.includes('Date Range');

  if (isDateRange) {
    if (endYear && endYear.length >= 4) {
      return `${prefix}${startYear} - ${endYear}`;
    }
    return `${prefix}${startYear}`;
  }

  return `${prefix}${startYear}`;
};

const formatYearOnlyPreview = (
  normalizedType: string,
  startYear: string,
  endYear: string
): string => {
  if (!startYear) return '';

  const isCirca = normalizedType.includes('Circa');
  const prefix = isCirca ? 'ca. ' : '';
  const isDateRange = normalizedType.includes('Date Range');

  if (isDateRange) {
    if (!endYear) return `${prefix}${startYear} - `;
    return `${prefix}${startYear} - ${endYear}`;
  }

  return `${prefix}${startYear}`;
};

const YearOnlyField = ({
  normalizedType,
  value,
  onChange,
  onAccept,
  onBlur,
  variant = 'mui',
}: YearOnlyFieldProps) => {
  const theme = useTheme();
  const isDateRange = normalizedType.includes('Date Range');
  const parsed = parseYearOnlyValue(normalizedType, value);
  const currentYear = new Date().getFullYear();

  // Local state holds intermediate typing so partial years (< 4 digits) are visible.
  // Without this, the controlled input wipes keystrokes because onChange only fires
  // for complete 4-digit years, leaving the parent value (and thus the input) empty.
  const [localStart, setLocalStart] = React.useState(parsed.startYear);
  const [localEnd, setLocalEnd] = React.useState(parsed.endYear);

  // Sync local state when the external value prop changes (e.g., server load, autosave)
  React.useEffect(() => {
    const next = parseYearOnlyValue(normalizedType, value);
    setLocalStart(next.startYear);
    setLocalEnd(next.endYear);
  }, [value, normalizedType]);

  const isFutureYear = (year: string): boolean => {
    const parsed = parseInt(year, 10);
    return !isNaN(parsed) && year.length >= 4 && parsed > currentYear;
  };

  const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStart = e.target.value;

    if (isFutureYear(newStart)) {
      return;
    }

    setLocalStart(newStart);
    const result = buildYearOnlyValue(normalizedType, newStart, localEnd);
    if (result !== '') {
      onChange?.(result);
    } else if (!newStart) {
      onChange?.('');
    }
  };

  const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEnd = e.target.value;

    if (isFutureYear(newEnd)) {
      return;
    }

    setLocalEnd(newEnd);
    const result = buildYearOnlyValue(normalizedType, localStart, newEnd);
    if (result !== '') {
      onChange?.(result);
    }
  };

  const handleBlur = () => {
    onBlur?.();
    onAccept?.();
  };

  // Prevent mouse-wheel / trackpad scroll from silently incrementing or decrementing
  // the year value while the input is focused (#1170). Blurring on wheel neutralizes
  // the native browser behavior (browsers only mutate number inputs on wheel when focused).
  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    (e.currentTarget as HTMLElement).blur();
  };

  const preview = formatYearOnlyPreview(normalizedType, localStart, localEnd);

  if (!normalizedType) return null;

  const startLabel = isDateRange ? en.datePicker.startDate : en.datePicker.date;

  return (
    <Box sx={variant === 'design' ? undefined : { mt: 2 }}>
      <Box sx={variant === 'design' ? { display: 'flex', gap: 2 } : { display: 'flex', gap: 2, mb: 2 }}>
        {variant === 'design' ? (
          <Box sx={{ flexGrow: 1 }}>
            <Field label={startLabel}>
              <input
                aria-label={startLabel}
                className="input"
                type="number"
                value={localStart}
                onChange={handleStartChange}
                onBlur={handleBlur}
                onWheel={handleWheel}
                min={1}
                max={currentYear}
                step={1}
                placeholder="YYYY"
              />
            </Field>
          </Box>
        ) : (
          <TextField
            label={startLabel}
            type="number"
            value={localStart}
            onChange={handleStartChange}
            onBlur={handleBlur}
            fullWidth
            inputProps={{ min: 1, max: currentYear, step: 1, onWheel: handleWheel }}
            placeholder="YYYY"
          />
        )}
        {isDateRange && (
          variant === 'design' ? (
            <Box sx={{ flexGrow: 1 }}>
              <Field label={en.datePicker.endDate}>
                <input
                  aria-label={en.datePicker.endDate}
                  className="input"
                  type="number"
                  value={localEnd}
                  onChange={handleEndChange}
                  onBlur={handleBlur}
                  onWheel={handleWheel}
                  min={1}
                  max={currentYear}
                  step={1}
                  placeholder="YYYY"
                />
              </Field>
            </Box>
          ) : (
            <TextField
              label={en.datePicker.endDate}
              type="number"
              value={localEnd}
              onChange={handleEndChange}
              onBlur={handleBlur}
              fullWidth
              inputProps={{ min: 1, max: currentYear, step: 1, onWheel: handleWheel }}
              placeholder="YYYY"
            />
          )
        )}
      </Box>

      {localStart && variant !== 'design' && (
        <TextField
          fullWidth
          label={en.datePicker.preview}
          value={preview}
          variant="outlined"
          InputProps={{ readOnly: true }}
          sx={{
            '& .MuiInputBase-input': {
              color: theme.palette.text.secondary
            }
          }}
        />
      )}
    </Box>
  );
};
